/**
 * KnowledgeSearchToolSelector Component (AC-822)
 *
 * Provides UI for adding and configuring a Knowledge Base Search tool on an agent.
 * Supports content type filtering, result limit, and similarity threshold configuration.
 *
 * Technical Design Reference: docs/technical/TDD-AC-819-agent-knowledge-base-access.md
 */

import React, { useCallback } from 'react';
import { BookOpen } from 'lucide-react';
import { AgentTool, KnowledgeContentType, KnowledgeSearchToolConfig } from '../../types';

const CONTENT_TYPES = [
  { value: 'document', label: 'Documents' },
  { value: 'general', label: 'General' },
  { value: 'project', label: 'Project' },
  { value: 'orchestration', label: 'Workflow' },
  { value: 'agent', label: 'Agent' },
  { value: 'model', label: 'Schema' },
] as const;

interface KnowledgeSearchToolSelectorProps {
  selectedTools: AgentTool[];
  onToolsChange: (tools: AgentTool[]) => void;
}

const KnowledgeSearchToolSelector: React.FC<KnowledgeSearchToolSelectorProps> = ({
  selectedTools,
  onToolsChange,
}) => {
  const existingTool = selectedTools.find(t => t.type === 'knowledge_search');
  const isEnabled = !!existingTool;
  const config: KnowledgeSearchToolConfig = (existingTool?.tool_config && 'content_types' in existingTool.tool_config ? existingTool.tool_config : {}) as KnowledgeSearchToolConfig;

  const handleToggle = useCallback(() => {
    if (isEnabled) {
      onToolsChange(selectedTools.filter(t => t.type !== 'knowledge_search'));
    } else {
      onToolsChange([
        ...selectedTools,
        {
          type: 'knowledge_search',
          name: 'Knowledge Search',
          description: 'Search the project Knowledge Base for relevant information',
          tool_config: {
            result_limit: 10,
          },
        },
      ]);
    }
  }, [isEnabled, selectedTools, onToolsChange]);

  const updateConfig = useCallback(
    (updates: Partial<KnowledgeSearchToolConfig>) => {
      const updatedTools = selectedTools.map(t => {
        if (t.type === 'knowledge_search') {
          return {
            ...t,
            tool_config: { ...t.tool_config, ...updates },
          };
        }
        return t;
      });
      onToolsChange(updatedTools);
    },
    [selectedTools, onToolsChange]
  );

  const handleContentTypeToggle = useCallback(
    (contentType: KnowledgeContentType) => {
      const current = config.content_types || [];
      const updated = current.includes(contentType)
        ? current.filter((ct): ct is KnowledgeContentType => ct !== contentType)
        : [...current, contentType];
      updateConfig({
        content_types: updated.length > 0 ? updated : undefined,
      });
    },
    [config.content_types, updateConfig]
  );

  const handleResultLimitChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = parseInt(e.target.value, 10);
      if (!isNaN(val) && val >= 1 && val <= 50) {
        updateConfig({ result_limit: val });
      }
    },
    [updateConfig]
  );

  const handleThresholdChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;
      if (val === '') {
        updateConfig({ similarity_threshold: undefined });
        return;
      }
      const num = parseFloat(val);
      if (!isNaN(num) && num >= 0 && num <= 1) {
        updateConfig({ similarity_threshold: num });
      }
    },
    [updateConfig]
  );

  return (
    <div data-testid="knowledge-search-tool-selector">
      {/* Toggle */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <BookOpen className="w-4 h-4 text-amber-600" />
          <span className="text-sm text-gray-700 dark:text-gray-300">
            Enable Knowledge Base Search
          </span>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            role="switch"
            aria-checked={isEnabled}
            checked={isEnabled}
            onChange={handleToggle}
            className="sr-only peer"
            data-testid="knowledge-search-toggle"
          />
          <div
            className={`w-11 h-6 rounded-full transition-colors peer-focus:ring-2 peer-focus:ring-primary-300 ${
              isEnabled
                ? 'bg-primary-600'
                : 'bg-gray-300 dark:bg-gray-600'
            }`}
          >
            <div
              className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                isEnabled ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </div>
        </label>
      </div>

      {!isEnabled && (
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Allow this agent to search your project's Knowledge Base for relevant documents and entries during execution.
        </p>
      )}

      {/* Configuration (visible when enabled) */}
      {isEnabled && (
        <div className="mt-3 space-y-3 pl-6 border-l-2 border-amber-200">
          {/* Content Type Filter */}
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Content Type Filter
              <span className="font-normal text-gray-500 dark:text-gray-400 ml-1">(optional - default: all types)</span>
            </label>
            <div className="flex flex-wrap gap-2">
              {CONTENT_TYPES.map(({ value, label }) => {
                const isChecked = config.content_types?.includes(value) || false;
                return (
                  <label
                    key={value}
                    className={`inline-flex items-center px-2 py-1 rounded text-xs cursor-pointer border ${
                      isChecked
                        ? 'bg-amber-50 border-amber-300 text-amber-800'
                        : 'bg-gray-50 dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => handleContentTypeToggle(value)}
                      className="sr-only"
                      data-testid={`content-type-${value}`}
                    />
                    {label}
                  </label>
                );
              })}
            </div>
          </div>

          {/* Result Limit */}
          <div>
            <label
              htmlFor="kb-result-limit"
              className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              Result Limit
              <span className="font-normal text-gray-500 dark:text-gray-400 ml-1">(1-50)</span>
            </label>
            <input
              id="kb-result-limit"
              type="number"
              min={1}
              max={50}
              value={config.result_limit ?? 10}
              onChange={handleResultLimitChange}
              className="w-20 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-800 dark:text-warm-white focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
              data-testid="result-limit-input"
            />
          </div>

          {/* Similarity Threshold */}
          <div>
            <label
              htmlFor="kb-similarity-threshold"
              className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              Similarity Threshold
              <span className="font-normal text-gray-500 dark:text-gray-400 ml-1">(optional, 0.0-1.0)</span>
            </label>
            <input
              id="kb-similarity-threshold"
              type="number"
              min={0}
              max={1}
              step={0.01}
              value={config.similarity_threshold ?? ''}
              onChange={handleThresholdChange}
              placeholder="None"
              className="w-20 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-800 dark:text-warm-white focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
              data-testid="similarity-threshold-input"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default KnowledgeSearchToolSelector;
